<template>
  <div class="mobile-search-button">
    <etg-button type="icon" :class="props.buttonClass" icon="search" size="small" :aria-label="$t('search.button')" @click="showSearchDrawer" />
    <n-drawer
      v-model:show="isActive"
      width="100%"
      placement="right"
      :z-index="zIndex.drawer"
      @after-enter="handleDrawerOpened"
      @after-leave="handleDrawerClosed"
    >
      <n-drawer-content>
        <div class="actions py-2 px-3 flex items-center sticky top-0 bg-white z-10">
          <etg-button type="icon" icon="arrow-left" @click="onArrowClick" />
          <etg-input
            ref="searchInputRef"
            v-model="inputValue"
            class="mr-2"
            :placeholder="props.searchInputPlaceholder"
            :input-props="{ enterKeyHint: 'search' }"
            size="small"
            clearable
            @submit="handleSubmit"
          >
          </etg-input>
        </div>
        <search-hint :keyword="keywordDebounced" @search-click="handleSearchClick" />
      </n-drawer-content>
    </n-drawer>
  </div>
</template>

<script setup lang="ts">
import { NDrawer, NDrawerContent } from 'naive-ui'

import SearchHint from '@/components/search/search-hint.vue'
import EtgInput from '@/components/form/etg-input.vue'

import { zIndex } from '@/theme.config'
import type { SearchClickPayload } from '@/types/search'

interface Props {
  searchInputPlaceholder: string
  buttonClass?: string
}

interface Emits {
  (event: 'submit', text: string): void
  (event: 'search-click', payload: SearchClickPayload): void
}

const props = withDefaults(defineProps<Props>(), {
  buttonClass: '',
})
const emit = defineEmits<Emits>()

const isActive = defineModel<boolean>({ default: false })
const keyword = defineModel<string>('keyword', { required: true })
const inputValue = ref<string>(keyword.value)
const keywordDebounced = refDebounced(inputValue, 300)
syncRefs(keyword, inputValue)

const searchInputRef = ref<Nullable<typeof EtgInput>>(null)

const showSearchDrawer = () => {
  isActive.value = true
}

function handleDrawerOpened() {
  // FIXME: 先用 setTimeout 解決點擊後無法 focus 的問題
  if (searchInputRef.value) {
    setTimeout(() => {
      searchInputRef.value!.focus()
    }, 100)
  }
}

function handleDrawerClosed() {
  // keep the close hook for the `after-leave` event for naive-ui drawer
}

function onArrowClick() {
  isActive.value = false
}

async function closeDrawer() {
  await nextTick()
  if (searchInputRef.value) searchInputRef.value.blur()
  requestAnimationFrame(() => (isActive.value = false))
  keyword.value = inputValue.value
}

async function handleSubmit(text?: string) {
  const submittedText = text || ''
  keyword.value = submittedText
  await closeDrawer()
  emit('submit', submittedText)
}

async function handleSearchClick({ item, keywordSource }: SearchClickPayload) {
  keyword.value = item.text || ''
  await closeDrawer()
  emit('search-click', { item, keywordSource })
}
</script>
